import { createSelector } from 'reselect'
import pluralize from 'pluralize'
import URI from 'urijs'
import moment from 'moment'
import { toArray, groupBy } from 'lodash-es'
import { humanizeSeconds, zipByRepetition } from '../../sharedLegacy/utils'

const getPerson = (state, uuid) =>
  state.audience.profiles.get(uuid, { profile: {} })
const getCustomFields = (state) => state.audience.customFields

export const getPersonIdentifier = (person) => {
  const { name, company, firstName, lastName } = person.profile
  if (name) {
    return name
  }

  const joinedName = [firstName, lastName]
    // Take only the truthy values.
    .filter((_name) => _name)
    .join(' ')

  if (joinedName) {
    return joinedName
  }

  if (company) {
    return `Viewer from ${company}`
  }

  return `Viewer #${person.identifier ?? ''}`
}

export const getPersonLocation = (profile = {}, showRegionName = undefined) => {
  const locations = []

  if (profile.city) {
    locations.push(profile.city)
  }

  if (showRegionName && profile.regionName) {
    locations.push(profile.regionName)
  }

  if (profile.country) {
    locations.push(profile.country)
  }

  return locations.join(', ')
}

export const getCompanyLocation = (profile) => {
  const result = []

  if (profile.companyCity) {
    result.push(profile.companyCity)
  }

  result.push(profile.companyCountry)

  return result.join(', ')
}

export const getDomain = (domainString) => {
  const parsedDomain = URI(domainString)
  const subdomain = parsedDomain.subdomain()
  const domain = parsedDomain.domain()

  if (subdomain && subdomain !== 'www') {
    return `${subdomain}.${domain}`
  }

  return domain
}

export const getPersonTimelineEngagement = createSelector(
  [getPerson],
  (person) => {
    if (person.timeline_engagement) {
      return humanizeSeconds(person.timeline_engagement, false)
    }

    return undefined
  }
)

export const getPersonTimelineCount = createSelector([getPerson], (person) => {
  if (person.timeline_count) {
    return `${person.timeline_count} ${pluralize(
      'video',
      person.timeline_count
    )} played`
  }

  return undefined
})

export const getPersonTimelineSessions = createSelector(
  [getPerson],
  (person) => {
    if (person.timeline_sessions) {
      return `${person.timeline_sessions} ${pluralize(
        'session',
        person.timeline_sessions
      )}`
    }

    return undefined
  }
)

export const getPersonRefererRecent = createSelector([getPerson], (person) => {
  if (person.referer_recent) {
    return {
      parsed: getDomain(person.referer_recent),
      link: person.referer_recent,
    }
  }

  return undefined
})

export const getPersonRefererFirst = createSelector([getPerson], (person) => {
  if (person.referer_first) {
    return {
      parsed: getDomain(person.referer_first),
      link: person.referer_first,
    }
  }

  return undefined
})

export const getPersonLocationSelector = createSelector(
  [getPerson],
  ({ profile }) => {
    if (profile && profile.countryCode) {
      return getPersonLocation(profile, true)
    }

    return undefined
  }
)

export const getPersonURL = createSelector([getPerson], ({ profile }) => {
  if (profile && profile.url) {
    return { parsed: getDomain(profile.url), link: profile.url }
  }

  return undefined
})

export const getPersonCompanySite = createSelector(
  [getPerson],
  ({ profile }) => {
    if (profile && profile.companySite) {
      return {
        parsed: getDomain(profile.companySite),
        link: profile.companySite,
      }
    }

    return undefined
  }
)

export const getPersonCompanyLocation = createSelector(
  [getPerson],
  ({ profile }) => {
    if (profile && profile.companyCountry) {
      return getCompanyLocation(profile)
    }

    return undefined
  }
)

export const getPersonCustomFields = createSelector(
  [getPerson, getCustomFields],
  ({ profile }, customFields) => {
    if (!customFields || !profile) {
      return []
    }

    return customFields.reduce((result, { key, label }) => {
      if (profile[key]) {
        result.push({ label, value: profile[key] })
      }

      return result
    }, [])
  }
)

export const formatPersonTimeline = (timeline, uuid) => {
  return {
    ...timeline,
    ranges: zipByRepetition(toArray(timeline.ranges)),
    rangesLength: timeline.ranges.length,
    personUuid: uuid,
  }
}

export const formatPersonTimelines = (person) => {
  if (person.timelines) {
    return person.timelines.map((timeline) =>
      formatPersonTimeline(timeline, person.uuid)
    )
  }

  return []
}

export const getPersonTimelines = createSelector([getPerson], (person) => {
  return formatPersonTimelines(person)
})

export const groupTimelinesByDate = (timelines, dateFormat) => {
  return groupBy(timelines, (timeline) => {
    const dt = moment(timeline.first)
    const date = dt.format(dateFormat)
    const today = moment().format(dateFormat)
    const yesterday = moment().add(-1, 'day').format(dateFormat)

    if (date === today) {
      return 'Today'
    }
    if (date === yesterday) {
      return 'Yesterday'
    }

    return date
  })
}

const convertTimeString = (time) => {
  if (time === 'Yesterday') {
    return moment().add(-1, 'day').toDate()
  }
  if (time === 'Today') {
    return new Date()
  }

  return moment(time, 'DD/MM//YYYY').toDate()
}

export const sortByDate = (a, b) => {
  return moment(convertTimeString(b)).diff(convertTimeString(a), 'minutes')
}

export const getPersonData = createSelector([getPerson], (person) => person)
